import CommonStyles from "@components/CommonStyles";
import { Form, Popconfirm } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ExtraFormLogin from "./ExtraForm";
import CaptchaWidget from "./CaptchaWidget";
import useFormValidation from "@base/hooks/useFormvalidation";
import { createSearchParams, useNavigate, useSearchParams } from "react-router-dom";
import useAuth from "@base/modules/auth/hooks/useAuth";
import useOTP from "@base/modules/user/account/hooks/useOTP";
import useTurnstile from "@base/hooks/useTurnstile";
import { baseRoutes } from "@base/routes/baseRoutes";

export default function LoginPasswordForm() {
  //! state
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const { login } = useFormValidation();
  const [searchParams] = useSearchParams();

  const phoneUrl = searchParams.get("phone");

  const initialValues = {
    password: "",
  };

  const { isLoading, error, handleLogin } = useAuth();
  const { handleGetOTP } = useOTP();

  const [isCaptchaOpen, setCaptchaOpen] = useState(false);

  const { widget: captchaWidget, token: turnstileToken, hasError: hasCaptchaError, resetWidget } = useTurnstile();

  // The token endpoint rejects a bad captcha with the same `invalid_grant` as a wrong password, told apart only by its text.
  const isCaptchaRejected = /captcha/i.test(error?.response?.data?.error_description ?? "");

  //! function
  const handleLoginSubmit = async (values: { password: string }) => {
    if (!values.password || !phoneUrl) {
      return;
    }

    if (!turnstileToken) {
      return;
    }

    await handleLogin(phoneUrl, values.password, turnstileToken);
    // A Turnstile token is single use, so a second attempt needs a fresh one.
    resetWidget();
  };

  const goToOTPScreen = () => {
    if (!phoneUrl) {
      return;
    }

    navigate({
      pathname: baseRoutes.login,
      search: createSearchParams({
        phone: phoneUrl,
        otp: "true",
        reset: "true",
      }).toString(),
    });
  };

  const handleForgotPassword = () => {
    if (!phoneUrl) {
      return;
    }

    setCaptchaOpen(true);
  };

  const handleCaptchaVerified = (turnstileToken: string) => {
    setCaptchaOpen(false);
    sendOtpThenContinue(turnstileToken);
  };

  // The OTP screen is only useful once an OTP is on its way; a 403/429 keeps the patient here.
  const sendOtpThenContinue = async (turnstileToken: string) => {
    if (!phoneUrl) {
      return;
    }

    if (await handleGetOTP(phoneUrl, i18n.resolvedLanguage ?? "", turnstileToken)) {
      goToOTPScreen();
    }
  };

  //! render
  const ForgotPassword = () => {
    return (
      <Popconfirm
        open={isCaptchaOpen}
        onOpenChange={(open) => setCaptchaOpen(open)}
        icon={null}
        title={t("LoginPage.CaptchaRequired")}
        description={isCaptchaOpen ? <CaptchaWidget onVerified={handleCaptchaVerified} /> : null}
        onCancel={() => setCaptchaOpen(false)}
        cancelText={t("Cancel")}
        okButtonProps={{ style: { display: "none" } }}
      >
        <span>
          <CommonStyles.TypographyUI onClick={handleForgotPassword} className="!text-common-inprogress cursor-pointer">
            {t("LoginPage.ForgotPassword")}
          </CommonStyles.TypographyUI>
        </span>
      </Popconfirm>
    );
  };

  return (
    <Form form={form} name="loginPassword" layout="vertical" initialValues={initialValues} onFinish={handleLoginSubmit}>
      <input hidden name="username" defaultValue={phoneUrl ?? ""} autoComplete="username" />

      {error ? (
        <CommonStyles.TypographyUI className="text-common-red500">
          {t(isCaptchaRejected ? "LoginPage.CaptchaError" : "LoginPage.invalidPass")}
        </CommonStyles.TypographyUI>
      ) : null}

      <CommonStyles.FormFieldUI
        name="password"
        rules={login.password()}
        component={
          <CommonStyles.InputUI
            autoComplete="new-password"
            use="password"
            placeholder={t("LoginPage.PlaceholderPassword")}
            variant="filled"
            label={t("LoginPage.Password")}
            extraTitle={<ForgotPassword />}
          />
        }
      />

      {/* <div className="flex gap-2 mb-4">
        <CommonStyles.CheckBoxUI titleCustom={t("LoginPage.SavePassowrd")} />
      </div> */}

      <div className="mb-4">{captchaWidget}</div>

      {hasCaptchaError ? (
        <div className="mb-4">
          <CommonStyles.TypographyUI className="text-common-red500">{t("LoginPage.CaptchaError")}</CommonStyles.TypographyUI>
        </div>
      ) : null}

      <div className="flex justify-center items-center">
        <CommonStyles.ButtonUI htmlType="submit" className="h-12 w-full" loading={isLoading} disabled={!turnstileToken}>
          <CommonStyles.TypographyUI use="text" className="text-white text-base font-semibold">
            {t("Login")}
          </CommonStyles.TypographyUI>
        </CommonStyles.ButtonUI>
      </div>

      <ExtraFormLogin />
    </Form>
  );
}
